import React, { useState, useEffect, useCallback } from 'react'
import { Image, Pressable, RefreshControl, useWindowDimensions } from 'react-native'
import {
  ArrowLeft, Store, ShoppingCart, Handshake, Building2, Amphora, Shirt, Tag,
  Camera, Package, ShoppingBag, ImagePlus, LucideIcon, ChevronRight, Receipt, Plus, DollarSign, Share2,
} from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  TopBar, IconButton, SectionHeader, StatCard, ActionCard, ListItem,
  Card, Text, Spinner, XStack, YStack, Divider, ScrollView, Sheet,
} from '@fliisi/pollen'
import { FullScreenLayout } from '../components/ScreenLayout'

const STORE_TYPE_ICONS: Record<string, LucideIcon> = {
  flea_market: ShoppingCart, consignment_store: Handshake, retail_store: Building2,
  antique_shop: Amphora, vintage_store: Shirt, thrift_store: Tag,
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MerchantDashboard'>
  route: RouteProp<RootStackParamList, 'MerchantDashboard'>
}

interface StoreInfo { id: string; name: string; store_type: string; address_city: string | null; logo_url?: string }
interface Stats { totalProducts: number; activeProducts: number; soldToday: number; revenueToday: number; pendingOffersCount: number; recentPostCount: number }
interface RecentSale { id: string; title: string; price: number; sold_at: string; image_url?: string }

export default function MerchantDashboardScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language } = useLanguage()
  const { width } = useWindowDimensions()

  const [store, setStore] = useState<StoreInfo | null>(null)
  const [stats, setStats] = useState<Stats>({ totalProducts: 0, activeProducts: 0, soldToday: 0, revenueToday: 0, pendingOffersCount: 0, recentPostCount: 0 })
  const [recentSales, setRecentSales] = useState<RecentSale[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showAddSheet, setShowAddSheet] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const { data: storeData } = await supabase
        .from('flea_markets').select('id, name, store_type, address_city, logo_url').eq('id', storeId).single()
      if (storeData) setStore(storeData)

      const { count: totalCount } = await supabase.from('products').select('*', { count: 'exact', head: true }).eq('flea_market_id', storeId)
      const { count: activeCount } = await supabase.from('products').select('*', { count: 'exact', head: true }).eq('flea_market_id', storeId).eq('status', 'for_sale')

      const today = new Date(); today.setHours(0, 0, 0, 0)
      const { data: soldData } = await supabase
        .from('products')
        .select('id, title, price, sold_at, product_images!product_images_product_id_fkey(image_url, is_primary)')
        .eq('flea_market_id', storeId).eq('status', 'sold').gte('sold_at', today.toISOString())
        .order('sold_at', { ascending: false }).limit(10)

      const soldToday = soldData?.length || 0
      const revenueToday = soldData?.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0) || 0
      const recentSalesWithImages = soldData?.map(p => {
        const primaryImage = (p.product_images as any[])?.find(img => img.is_primary) || (p.product_images as any[])?.[0]
        return { id: p.id, title: p.title, price: p.price, sold_at: p.sold_at, image_url: primaryImage?.image_url }
      }) || []
      setRecentSales(recentSalesWithImages.slice(0, 5))

      let pendingOffersCount = 0
      try {
        const { count, error } = await supabase.from('seller_offers').select('*', { count: 'exact', head: true }).eq('store_id', storeId).eq('status', 'pending')
        if (!error) pendingOffersCount = count || 0
      } catch {}

      let recentPostCount = 0
      try {
        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
        const { count: postCount, error: postError } = await supabase
          .from('social_posts')
          .select('*', { count: 'exact', head: true })
          .eq('store_id', storeId)
          .gte('created_at', sevenDaysAgo.toISOString())
        if (!postError) recentPostCount = postCount || 0
      } catch {}

      setStats({ totalProducts: totalCount || 0, activeProducts: activeCount || 0, soldToday, revenueToday, pendingOffersCount, recentPostCount })
    } catch (error) { console.error('Error fetching dashboard data:', error) }
    finally { setLoading(false); setRefreshing(false) }
  }, [storeId])

  useEffect(() => { fetchData() }, [fetchData])

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString(language === 'fi' ? 'fi-FI' : 'en-US', { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return <YStack f={1} jc="center" ai="center" bg="$background"><Spinner size="large" /></YStack>
  }

  const IconComponent = STORE_TYPE_ICONS[store?.store_type || ''] || Store

  return (
    <FullScreenLayout>
      <TopBar
        title={store?.name || ''}
        left={
          <IconButton variant="ghost" onPress={() => navigation.navigate('MerchantStoreSelect')}>
            <ArrowLeft size={18} color="#C07A5F" />
          </IconButton>
        }
        right={
          store?.logo_url ? (
            <Image source={{ uri: store.logo_url }} style={{ width: 36, height: 36, borderRadius: 8 }} />
          ) : (
            <YStack w={36} h={36} br="$md" bg="rgba(192,122,95,0.1)" jc="center" ai="center">
              <IconComponent size={20} color="#C07A5F" />
            </YStack>
          )
        }
      />

      <ScrollView
        f={1}
        contentContainerStyle={{ maxWidth: 768, width: '100%', alignSelf: 'center' as const, paddingBottom: 32 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData() }} colors={['#C07A5F']} tintColor="#C07A5F" />
        }
      >
        {/* Today's Summary */}
        <Card variant="elevated" mx="$md" mt="$md">
          <SectionHeader
            title={t('today') || 'Today'}
            actionLabel={t('view_all') || 'View all'}
            onAction={() => navigation.navigate('MerchantActivity', { storeId, tab: 'sales' })}
          />
          <XStack ai="center" mt="$sm">
            <StatCard icon={<ShoppingBag size={20} color="#748C70" />} value={String(stats.soldToday)} label={t('sales') || 'Sales'} f={1} />
            <YStack w={1} h={50} bg="#E0E2DB" />
            <StatCard icon={<DollarSign size={20} color="#C07A5F" />} value={`${stats.revenueToday.toFixed(0)} €`} label={t('revenue') || 'Revenue'} f={1} />
            <YStack w={1} h={50} bg="#E0E2DB" />
            <StatCard icon={<Package size={20} color="#3B82F6" />} value={String(stats.activeProducts)} label={t('for_sale') || 'For sale'} f={1} />
          </XStack>
        </Card>

        {/* Alerts */}
        {stats.pendingOffersCount > 0 && (
          <YStack mx="$md" mt="$md">
            <ListItem
              icon={<YStack w={40} h={40} br={10} bg="rgba(192,122,95,0.1)" jc="center" ai="center"><Package size={20} color="#C07A5F" /></YStack>}
              title={t('pending_offers') || 'Pending offers'}
              subtitle={`${stats.pendingOffersCount} ${t('offers_to_review') || 'offers to review'}`}
              right={<ChevronRight size={18} color="#858B80" />}
              onPress={() => navigation.navigate('MerchantActivity', { storeId, tab: 'offers' })}
              bg="$backgroundCard"
              br="$md"
            />
          </YStack>
        )}

        {/* Quick Actions */}
        <YStack mx="$md" mt="$md">
          <Text preset="h3" mb="$sm">{t('quick_actions') || 'Quick Actions'}</Text>
          <XStack flexWrap="wrap" gap={10}>
            <ActionCard f={1} minWidth="47%" icon={<ShoppingBag size={24} color="#fff" />} title={t('kassa') || 'Kassa'} description={t('quick_sale_desc') || 'Start a new sale'} onPress={() => navigation.navigate('MerchantPOS', { storeId })} />
            <ActionCard f={1} minWidth="47%" icon={<Package size={24} color="#fff" />} title={t('inventory') || 'Varasto'} description={`${stats.totalProducts} ${t('products') || 'products'}`} onPress={() => navigation.navigate('StoreInventory', { storeId })} />
            <ActionCard f={1} minWidth="47%" icon={<Plus size={24} color="#fff" />} title={t('add_product') || 'Lisää tuote'} description={t('add_product_desc') || 'Camera or gallery'} onPress={() => setShowAddSheet(true)} />
            <ActionCard f={1} minWidth="47%" icon={<Receipt size={24} color="#fff" />} title={t('transactions') || 'Tapahtumat'} description={t('transaction_history') || 'Transaction History'} onPress={() => navigation.navigate('StoreTransactions', { storeId })} />
          </XStack>
        </YStack>

        {/* Add Product Sheet */}
        <Sheet open={showAddSheet} onOpenChange={setShowAddSheet} snapPoints={[30]} noHandle={false}>
          <YStack px="$md" pt="$sm" pb="$xl" gap="$sm">
            <Text preset="h3" mb="$xs">{t('add_product') || 'Lisää tuote'}</Text>
            <ActionCard icon={<Camera size={24} color="#fff" />} title={t('camera') || 'Kamera'} description={t('take_photo_desc') || 'Take a photo'} onPress={() => { setShowAddSheet(false); navigation.navigate('StoreCamera', { storeId }) }} />
            <ActionCard icon={<ImagePlus size={24} color="#fff" />} title={t('from_gallery') || 'Galleria'} description={t('pick_from_gallery_desc') || 'Pick from gallery'} onPress={() => { setShowAddSheet(false); navigation.navigate('StoreCamera', { storeId, pickFromGallery: true }) }} />
          </YStack>
        </Sheet>

        {/* Social Media Widget */}
        <Card variant="elevated" mx="$md" mt="$md">
          <XStack ai="center" jc="space-between">
            <XStack ai="center" gap="$sm" f={1}>
              <YStack w={40} h={40} br={10} bg="rgba(192,122,95,0.1)" jc="center" ai="center">
                <Share2 size={20} color="#C07A5F" />
              </YStack>
              <YStack f={1}>
                <Text preset="label" color="$color">Sosiaalinen media</Text>
                <Text preset="caption" tone="secondary">
                  {stats.recentPostCount > 0
                    ? `${stats.recentPostCount} julkaisua viimeisen 7 pv aikana`
                    : 'Ei julkaisuja vielä'}
                </Text>
              </YStack>
            </XStack>
            <Pressable onPress={() => navigation.navigate('SocialAccounts', { storeId, tab: 'create' })}>
              <XStack bg="rgba(192,122,95,0.1)" px={12} py={6} br="$full" ai="center" gap={4}>
                <Plus size={14} color="#C07A5F" />
                <Text fontSize={12} fontWeight="600" color="#C07A5F">Luo julkaisu</Text>
              </XStack>
            </Pressable>
          </XStack>
        </Card>

        {/* Recent Sales */}
        {recentSales.length > 0 && (
          <YStack mx="$md" mt="$md">
            <SectionHeader
              title={t('recent_sales') || 'Recent Sales'}
              actionLabel={t('view_all') || 'View all'}
              onAction={() => navigation.navigate('MerchantActivity', { storeId, tab: 'sales' })}
            />
            <Card variant="elevated">
              {recentSales.map((sale, index) => (
                <React.Fragment key={sale.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    icon={
                      sale.image_url ? (
                        <Image source={{ uri: sale.image_url }} style={{ width: 40, height: 40, borderRadius: 8 }} />
                      ) : (
                        <YStack w={40} h={40} br="$md" bg="#F2F0E9" jc="center" ai="center">
                          <Package size={16} color="#858B80" />
                        </YStack>
                      )
                    }
                    title={sale.title}
                    subtitle={formatTime(sale.sold_at)}
                    right={<Text preset="label" color="#C07A5F">{sale.price.toFixed(2)} €</Text>}
                  />
                </React.Fragment>
              ))}
            </Card>
          </YStack>
        )}

        <YStack h={40} />
      </ScrollView>
    </FullScreenLayout>
  )
}
